/**
 * Shared date utilities for consistent UTC date handling across the application
 */

import { formatInTimeZone } from 'date-fns-tz';

/**
 * Combines a date string (YYYY-MM-DD) and time string (HH:mm) into a Date object in UTC
 * @param dateStr - Date string in YYYY-MM-DD format
 * @param timeStr - Time string in HH:mm format
 * @returns Date object combining the date and time in UTC
 */
export const createUTCDate = (dateStr: string, timeStr: string): Date => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const [year, month, day] = dateStr.split('-').map(Number);

  return new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0));
};

/**
 * Formats a date to display format in UTC
 * @param date - Date object to format
 * @returns Object with formatted day and time
 */
export const formatDateTimeUTC = (date: Date) => {
  const day = formatInTimeZone(date, 'UTC', 'EEE dd MMM, yyyy');
  const time = formatInTimeZone(date, 'UTC', 'h:mm a');
  return { day, time };
};

/**
 * Formats a date to UTC date string (YYYY-MM-DD)
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format
 */
export const formatDateStringUTC = (date: Date): string => {
  return formatInTimeZone(date, 'UTC', 'yyyy-MM-dd');
};

/**
 * Formats a date to UTC day display (e.g., "Wed 19 Jun, 2025")
 * @param date - Date object to format
 * @returns Formatted day string
 */
export const formatDayUTC = (date: Date): string => {
  return formatInTimeZone(date, 'UTC', 'EEE dd MMM, yyyy');
};

/**
 * Formats a date to UTC time display (e.g., "9:00 AM")
 * @param date - Date object to format
 * @returns Formatted time string
 */
export const formatTimeUTC = (date: Date): string => {
  return formatInTimeZone(date, 'UTC', 'h:mm a');
};

/**
 * Formats a date to UTC time input format (HH:mm)
 * @param date - Date object to format
 * @returns Time string in HH:mm format
 */
export const formatTimeInputUTC = (date: Date): string => {
  return formatInTimeZone(date, 'UTC', 'HH:mm');
};

/**
 * Formats a date to UTC datetime string (YYYY-MM-DD HH:mm)
 * @param date - Date object to format
 * @returns Datetime string in YYYY-MM-DD HH:mm format
 */
export const formatDateTimeStringUTC = (date: Date): string => {
  return formatInTimeZone(date, 'UTC', 'yyyy-MM-dd HH:mm');
};

/**
 * Formats a date to UTC ISO date string (YYYY-MM-DDTHH:mm:ss.sssZ)
 * @param date - Date object to format
 * @returns ISO date string
 */
export const formatISOStringUTC = (date: Date): string => {
  return date.toISOString();
};

/**
 * Parses an ISO date string into a Date object
 * @param isoString - ISO date string
 * @returns Date object
 */
export const parseISODate = (isoString: string): Date => {
  return new Date(isoString);
};

/**
 * Checks if a date is in the past compared to now
 * @param date - Date to check
 * @returns True if date is in the past
 */
export const isDateInPast = (date: Date): boolean => {
  return date < new Date();
};

/**
 * Checks if the end date/time is after the start date/time
 * @param startDate - Start date/time
 * @param endDate - End date/time
 * @returns boolean indicating if end is after start
 */
export const isEndAfterStart = (startDate: Date, endDate: Date): boolean => {
  return endDate > startDate;
};

/**
 * Adds minutes to a date
 * @param date - Base date
 * @param minutes - Minutes to add
 * @returns New date with added minutes
 */
export const addMinutesToDate = (date: Date, minutes: number): Date => {
  return new Date(date.getTime() + minutes * 60000);
};

/**
 * Adds hours to a date
 * @param date - Base date
 * @param hours - Hours to add
 * @returns New date with added hours
 */
export const addHoursToDate = (date: Date, hours: number): Date => {
  return addMinutesToDate(date, hours * 60);
};

/**
 * Gets the difference between two dates in minutes
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Difference in minutes
 */
export const getMinutesDifference = (startDate: Date, endDate: Date): number => {
  return Math.round((endDate.getTime() - startDate.getTime()) / 60000);
};

/**
 * Gets the difference between two dates in hours
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Difference in hours
 */
export const getHoursDifference = (startDate: Date, endDate: Date): number => {
  return getMinutesDifference(startDate, endDate) / 60;
};

/**
 * Formats a date string to a human-readable time ago format
 * @param dateString - ISO date string
 * @returns Formatted time ago string (e.g., "2h ago", "Just now")
 */
export const formatTimeAgo = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const now = new Date();

    // Handle invalid dates
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', dateString);
      return 'Unknown time';
    }

    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  } catch (error) {
    console.warn('Error formatting time:', error);
    return 'Unknown time';
  }
};
